import { useState, useEffect } from "react";
import { toast } from "sonner";
import { User, AtSign, Hash, Mail, Phone, CalendarDays, Copy, Lock, Check } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Spinner } from "../components/common";

function ReadOnlyRow({ icon: Icon, label, value, note, onCopy }) {
  return (
    <div className="flex items-center gap-3 py-3.5">
      <div className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-primary shrink-0"><Icon size={16} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm font-medium truncate">{value}</div>
        {note && <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1"><Lock size={11} /> {note}</div>}
      </div>
      {onCopy && (
        <button data-testid="pi-copy-cashtag" onClick={onCopy} className="grid place-items-center w-8 h-8 rounded-full text-primary transition-colors hover:bg-secondary"><Copy size={15} /></button>
      )}
    </div>
  );
}

export default function PersonalInfo() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.full_name || user.display_name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [cashtag, setCashtag] = useState((user.cash_tag || "").replace(/^\$/, ""));
  const [ctStatus, setCtStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ctBusy, setCtBusy] = useState(false);
  const [emailStep, setEmailStep] = useState("idle"); // idle | request | confirm
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const loadCtStatus = async () => {
    try { const { data } = await api.get("/auth/profile/cashtag/status"); setCtStatus(data); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadCtStatus(); }, []);

  const dirty = (name.trim() !== (user.full_name || user.display_name || "")) || (phone.trim() !== (user.phone || ""));

  const save = async () => {
    if (!name.trim()) { toast.error("Full name can't be empty"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", { display_name: name.trim(), full_name: name.trim(), phone: phone.trim() });
      setUser(data);
      toast.success("Changes saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const saveCashtag = async () => {
    const clean = cashtag.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    if (clean.length < 3) { toast.error("Cashtag must be at least 3 letters or numbers"); return; }
    if (`$${clean}` === user.cash_tag) { toast.error("That's already your cashtag"); return; }
    setCtBusy(true);
    try {
      const { data } = await api.post("/auth/profile/cashtag", { cash_tag: clean });
      setUser(data.user); toast.success("Cashtag updated");
      await loadCtStatus();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCtBusy(false); }
  };

  const noChangesLeft = ctStatus && ctStatus.changes_remaining === 0;
  const inp = "w-full h-12 px-3 mt-1 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30";

  const resetEmailFlow = () => { setEmailStep("idle"); setNewEmail(""); setEmailPw(""); setEmailCode(""); setEmailHint(""); };

  const requestEmailChange = async () => {
    const e = newEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Enter a valid email address"); return; }
    if (!emailPw) { toast.error("Enter your password"); return; }
    setEmailBusy(true);
    try {
      const { data } = await api.post("/auth/email/change/request", { new_email: e, password: emailPw });
      if (data.status === "code_sent") {
        setEmailHint(data.email_hint || e);
        setEmailStep("confirm");
        toast.success("Verification code sent to your new email");
      } else {
        toast.error(data.message || "Couldn't send the code right now");
      }
    } catch (err) { toast.error(errMsg(err)); }
    finally { setEmailBusy(false); }
  };

  const confirmEmailChange = async () => {
    if (!/^\d{4,8}$/.test(emailCode)) { toast.error("Enter the code from your email"); return; }
    setEmailBusy(true);
    try {
      const { data } = await api.post("/auth/email/change/confirm", { code: emailCode });
      setUser(data);
      toast.success("Email address updated");
      resetEmailFlow();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setEmailBusy(false); }
  };

  return (
    <div className="animate-fade-up pb-4" data-testid="personal-info-page">
      <PageHeader title="Personal Information" subtitle="Manage your personal and contact details" />

      {/* Editable fields */}
      <div className="rounded-3xl bg-card border border-border p-5 mb-4">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><User size={13} /> Full name</label>
            <input data-testid="pi-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full legal name" className={inp} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><Phone size={13} /> Phone number</label>
            <input data-testid="pi-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000" className={inp} />
          </div>
        </div>
        <button data-testid="pi-save-btn" onClick={save} disabled={busy || !dirty}
          className="w-full h-12 mt-5 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center disabled:opacity-50">
          {busy ? <Spinner className="w-5 h-5" /> : "Save Changes"}
        </button>
      </div>

      {/* Cash Tag (restricted, own save + limits) */}
      <div className="rounded-3xl bg-card border border-border p-5 mb-4">
        <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><Hash size={13} /> Cash Tag</label>
        <div className="flex gap-2 mt-1">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">$</span>
            <input data-testid="pi-cashtag-input" value={cashtag}
              onChange={(e) => setCashtag(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toLowerCase().slice(0, 15))}
              placeholder="yourname"
              disabled={noChangesLeft}
              className="w-full h-12 pl-7 pr-3 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30 font-mono disabled:opacity-60" />
          </div>
          <button data-testid="pi-save-cashtag-btn" onClick={saveCashtag}
            disabled={ctBusy || noChangesLeft || `$${cashtag}` === user.cash_tag}
            className="px-4 h-12 rounded-xl bg-primary text-white font-semibold text-sm disabled:opacity-50">
            {ctBusy ? <Spinner className="w-4 h-4" /> : "Save"}
          </button>
        </div>
        {ctStatus && (
          <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="pi-cashtag-limit-note">
            {ctStatus.changes_remaining > 0
              ? `${ctStatus.changes_remaining} of ${ctStatus.changes_allowed} changes left in ${ctStatus.window_days} days · letters & numbers, 3–15 chars`
              : `No changes left — you can change again on ${ctStatus.next_available_at ? new Date(ctStatus.next_available_at).toLocaleDateString() : "later"}`}
          </p>
        )}
      </div>

      {/* Email with verification change flow */}
      <div className="rounded-3xl bg-card border border-border p-5 mb-4" data-testid="pi-email-card">
        <div className="flex items-center gap-3">
          <div className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-primary shrink-0"><Mail size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Email address</div>
            <div className="text-sm font-medium truncate">{user.email}</div>
          </div>
          <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0 ${user.email_verified ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
            {user.email_verified && <Check size={12} strokeWidth={3} />}{user.email_verified ? "Verified" : "Not Verified"}
          </span>
        </div>

        {emailStep === "idle" && (
          <button data-testid="pi-change-email-btn" onClick={() => setEmailStep("request")}
            className="w-full h-11 mt-4 rounded-xl bg-secondary text-primary font-semibold text-sm">Change email address</button>
        )}

        {emailStep === "request" && (
          <div className="mt-4 space-y-3 border-t border-border pt-4">
            <p className="text-[12px] text-muted-foreground">We'll send a 6-digit code to your new email to confirm it's yours.</p>
            <input data-testid="pi-new-email-input" type="email" placeholder="New email address" value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)} className="w-full h-12 px-4 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
            <input data-testid="pi-email-password-input" type="password" placeholder="Your current password" value={emailPw}
              onChange={(e) => setEmailPw(e.target.value)} className="w-full h-12 px-4 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
            <div className="flex gap-2">
              <button data-testid="pi-email-cancel-btn" onClick={resetEmailFlow} className="flex-1 h-11 rounded-xl bg-secondary font-semibold text-sm">Cancel</button>
              <button data-testid="pi-email-send-code-btn" onClick={requestEmailChange} disabled={emailBusy}
                className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center">
                {emailBusy ? <Spinner className="w-5 h-5" /> : "Send code"}
              </button>
            </div>
          </div>
        )}

        {emailStep === "confirm" && (
          <div className="mt-4 space-y-3 border-t border-border pt-4">
            <p className="text-[12px] text-muted-foreground">Enter the 6-digit code we sent to <span className="font-mono text-foreground/80">{emailHint}</span>.</p>
            <input data-testid="pi-email-code-input" inputMode="numeric" maxLength={6} placeholder="______" value={emailCode}
              onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full h-12 px-4 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30 text-center tracking-[0.5em] font-mono text-lg" />
            <div className="flex gap-2">
              <button data-testid="pi-email-back-btn" onClick={() => setEmailStep("request")} className="flex-1 h-11 rounded-xl bg-secondary font-semibold text-sm">Back</button>
              <button data-testid="pi-email-confirm-btn" onClick={confirmEmailChange} disabled={emailBusy}
                className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center">
                {emailBusy ? <Spinner className="w-5 h-5" /> : "Confirm & update"}
              </button>
            </div>
            <button data-testid="pi-email-resend-btn" onClick={requestEmailChange} disabled={emailBusy} className="w-full text-xs font-semibold text-primary">Resend code</button>
          </div>
        )}
      </div>

      {/* Read-only details */}
      <div className="rounded-3xl bg-card border border-border px-5 py-1 divide-y divide-border">
        <ReadOnlyRow icon={AtSign} label="Username" value={`@${user.username}`} note="Your username can't be changed" />
        <ReadOnlyRow icon={CalendarDays} label="Account created" value={user.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—"} />
      </div>
    </div>
  );
}
